import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API = 'https://csksportswear.in/backend/api';

function SiteSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('logo');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [adminLogoPreview, setAdminLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingAdminLogo, setUploadingAdminLogo] = useState(false);
  const logoRef = useRef(null);
  const adminLogoRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('csk_token');
  const user = JSON.parse(localStorage.getItem('csk_user') || '{}');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/admin/dashboard'); return; }
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings.php?action=get`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setSettings(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings.php?action=update`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { alert('Error saving!'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (file, type) => {
    if (!file) return;
    if (type === 'site') setUploadingLogo(true);
    else setUploadingAdminLogo(true);

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', type);

      const res = await axios.post(`${API}/upload.php?action=logo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        const key = type === 'site' ? 'site_logo' : 'admin_logo';
        const newSettings = { ...settings, [key]: res.data.url };
        setSettings(newSettings);

        // Auto save
        await axios.post(`${API}/settings.php?action=update`, newSettings, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (type === 'site') setLogoPreview(res.data.url);
        else setAdminLogoPreview(res.data.url);

        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      alert('Logo upload failed!');
    } finally {
      if (type === 'site') setUploadingLogo(false);
      else setUploadingAdminLogo(false);
    }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const inputStyle = {
    width: '100%', padding: '10px 14px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: 'white',
    fontSize: '14px', outline: 'none',
    boxSizing: 'border-box', fontFamily: 'Inter, sans-serif',
  };

  const textareaStyle = { ...inputStyle, resize: 'vertical', lineHeight: 1.6 };
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' };

  const tabs = [
    { id: 'logo', label: '🖼️ Logo & Brand' },
    { id: 'hero', label: '🏠 Hero' },
    { id: 'contact', label: '📞 Contact' },
    { id: 'about', label: 'ℹ️ About' },
    { id: 'stats', label: '📊 Stats' },
    { id: 'footer', label: '📄 Footer' },
  ];

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: '🏠' },
    { path: '/admin/inventory', label: 'Inventory', icon: '📦' },
    { path: '/admin/pos', label: 'POS Billing', icon: '🧾' },
    { path: '/admin/reports', label: 'Reports', icon: '📊' },
    { path: '/admin/gallery', label: 'Gallery', icon: '🖼️' },
    { path: '/admin/enquiries', label: 'Enquiries', icon: '📩' },
    { path: '/admin/settings', label: 'Settings', icon: '⚙️' },
    { path: '/admin/users', label: 'Users', icon: '👥' },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0A0A0A', fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        .set-sidebar { width: 200px; background: #000; border-right: 1px solid rgba(255,255,255,0.08); display: flex; flex-direction: column; position: fixed; top: 0; left: 0; bottom: 0; z-index: 100; transition: transform 0.3s ease; }
        .set-main { margin-left: 200px; flex: 1; padding: 32px; }
        .set-mobile-hdr { display: none; background: #000; padding: 12px 16px; position: fixed; top: 0; left: 0; right: 0; z-index: 99; border-bottom: 1px solid rgba(255,255,255,0.08); align-items: center; justify-content: space-between; }
        .set-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 99; }
        @media (max-width: 768px) {
          .set-sidebar { transform: translateX(-100%); }
          .set-sidebar.open { transform: translateX(0); }
          .set-main { margin-left: 0; padding: 72px 16px 16px; }
          .set-mobile-hdr { display: flex; }
          .set-overlay.show { display: block; }
          .set-tabs { flex-wrap: wrap !important; }
          .logo-grid { grid-template-columns: 1fr !important; }
          .stats-row { grid-template-columns: 1fr !important; }
        }
        .set-nav-link { display: flex; align-items: center; gap: 10px; padding: 12px 16px; color: #475569; font-size: 13px; font-weight: 500; text-decoration: none; border-left: 3px solid transparent; transition: all 0.2s; }
        .set-nav-link:hover { color: white; background: rgba(255,255,255,0.05); }
        .set-nav-link.active { color: white; background: rgba(0,102,255,0.1); border-left-color: #0066FF; }
        .logo-upload-zone { border: 2px dashed rgba(0,102,255,0.3); border-radius: 12px; padding: 32px; text-align: center; cursor: pointer; transition: all 0.2s; background: rgba(0,102,255,0.02); }
        .logo-upload-zone:hover { border-color: #0066FF; background: rgba(0,102,255,0.05); }
        input:focus, textarea:focus { border-color: rgba(0,102,255,0.5) !important; }
      `}</style>

      {/* Mobile Header */}
      <div className="set-mobile-hdr">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {settings.admin_logo ? (
            <img src={settings.admin_logo} alt="logo" style={{ height: '28px', objectFit: 'contain' }} />
          ) : (
            <span style={{ color: '#DC2626', fontSize: '20px' }}>★</span>
          )}
          <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 800, color: 'white' }}>
            {settings.site_name || 'CSK'}
          </span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer' }}>☰</button>
      </div>

      <div className={`set-overlay ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <div className={`set-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {settings.admin_logo ? (
              <img src={settings.admin_logo} alt="logo" style={{ height: '32px', objectFit: 'contain' }} />
            ) : (
              <span style={{ color: '#DC2626', fontSize: '20px' }}>★</span>
            )}
            <div>
              <div style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 800, letterSpacing: '2px', color: 'white' }}>
                {settings.site_name || 'CSK'}
              </div>
              <div style={{ fontSize: '8px', color: '#475569', letterSpacing: '1px' }}>ADMIN PANEL</div>
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>{user.name}</div>
          <div style={{ fontSize: '11px', color: '#0066FF', textTransform: 'uppercase', letterSpacing: '1px' }}>{user.role}</div>
        </div>
        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
          {menuItems.map(item => (
            <Link key={item.path} to={item.path}
              className={`set-nav-link ${window.location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}>
              <span>{item.icon}</span>{item.label}
            </Link>
          ))}
        </nav>
        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={() => { localStorage.clear(); navigate('/login'); }} style={{ width: '100%', padding: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', color: '#DC2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>🚪 Logout</button>
        </div>
      </div>

      {/* Main */}
      <div className="set-main">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontFamily: 'Montserrat', fontSize: '28px', fontWeight: 800, color: 'white', marginBottom: '4px' }}>Site Settings</h1>
            <p style={{ color: '#475569', fontSize: '14px' }}>Edit website content and branding</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            {saved && (
              <div style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: '8px', padding: '8px 16px', color: '#16A34A', fontSize: '13px', fontWeight: 600 }}>
                ✅ Saved!
              </div>
            )}
            <a href="/" target="_blank" rel="noreferrer" style={{ padding: '10px 20px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}>
              👁 Preview
            </a>
            <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: 'linear-gradient(135deg, #0066FF, #0052CC)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : '💾 Save All'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="set-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '10px 20px', border: 'none', borderRadius: '8px', background: activeTab === tab.id ? '#0066FF' : 'rgba(255,255,255,0.05)', color: activeTab === tab.id ? 'white' : '#475569', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#475569', padding: '60px' }}>Loading...</div>
        ) : (
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '32px' }}>

            {/* ── LOGO TAB ── */}
            {activeTab === 'logo' && (
              <div>
                <h2 style={{ fontFamily: 'Montserrat', fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '24px' }}>🖼️ Logo & Brand Identity</h2>

                <div className="logo-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '32px' }}>

                  {/* Website Logo */}
                  <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '24px' }}>
                    <h3 style={{ fontFamily: 'Montserrat', fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '6px' }}>Website Logo</h3>
                    <p style={{ color: '#475569', fontSize: '12px', marginBottom: '20px' }}>Shows in Navbar on your website</p>

                    {/* Current Logo */}
                    <div style={{ background: '#000', borderRadius: '10px', padding: '20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100px', border: '1px solid rgba(255,255,255,0.06)' }}>
                      {(logoPreview || settings.site_logo) ? (
                        <img src={logoPreview || settings.site_logo} alt="Site Logo" style={{ maxHeight: '80px', maxWidth: '200px', objectFit: 'contain' }} />
                      ) : (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                            <span style={{ color: '#DC2626', fontSize: '24px' }}>★</span>
                            <span style={{ fontFamily: 'Montserrat', fontWeight: 900, fontSize: '18px', letterSpacing: '3px', color: 'white' }}>CSK</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px' }}>Default Logo</div>
                        </div>
                      )}
                    </div>

                    <div className="logo-upload-zone" onClick={() => logoRef.current?.click()}>
                      {uploadingLogo ? (
                        <div style={{ color: '#0066FF', fontWeight: 600 }}>Uploading...</div>
                      ) : (
                        <>
                          <div style={{ fontSize: '28px', marginBottom: '8px' }}>📤</div>
                          <div style={{ color: 'white', fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>Click to upload logo</div>
                          <div style={{ color: '#475569', fontSize: '12px' }}>PNG, SVG recommended — Transparent background best</div>
                        </>
                      )}
                      <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => { if (e.target.files[0]) { handleLogoUpload(e.target.files[0], 'site'); const r = new FileReader(); r.onload = ev => setLogoPreview(ev.target.result); r.readAsDataURL(e.target.files[0]); } }} />
                    </div>

                    {(settings.site_logo || logoPreview) && (
                      <button onClick={() => { update('site_logo', ''); setLogoPreview(null); }} style={{ marginTop: '12px', width: '100%', padding: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', color: '#DC2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                        🗑 Remove Logo (Use Default)
                      </button>
                    )}
                  </div>

                  {/* Admin Logo */}
                  <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '24px' }}>
                    <h3 style={{ fontFamily: 'Montserrat', fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '6px' }}>Admin Panel Logo</h3>
                    <p style={{ color: '#475569', fontSize: '12px', marginBottom: '20px' }}>Shows in Admin sidebar</p>

                    <div style={{ background: '#000', borderRadius: '10px', padding: '20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100px', border: '1px solid rgba(255,255,255,0.06)' }}>
                      {(adminLogoPreview || settings.admin_logo) ? (
                        <img src={adminLogoPreview || settings.admin_logo} alt="Admin Logo" style={{ maxHeight: '80px', maxWidth: '200px', objectFit: 'contain' }} />
                      ) : (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                            <span style={{ color: '#DC2626', fontSize: '24px' }}>★</span>
                            <span style={{ fontFamily: 'Montserrat', fontWeight: 900, fontSize: '18px', letterSpacing: '3px', color: 'white' }}>CSK</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px' }}>Default Logo</div>
                        </div>
                      )}
                    </div>

                    <div className="logo-upload-zone" onClick={() => adminLogoRef.current?.click()}>
                      {uploadingAdminLogo ? (
                        <div style={{ color: '#0066FF', fontWeight: 600 }}>Uploading...</div>
                      ) : (
                        <>
                          <div style={{ fontSize: '28px', marginBottom: '8px' }}>📤</div>
                          <div style={{ color: 'white', fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>Click to upload logo</div>
                          <div style={{ color: '#475569', fontSize: '12px' }}>PNG, SVG recommended</div>
                        </>
                      )}
                      <input ref={adminLogoRef} type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => { if (e.target.files[0]) { handleLogoUpload(e.target.files[0], 'admin'); const r = new FileReader(); r.onload = ev => setAdminLogoPreview(ev.target.result); r.readAsDataURL(e.target.files[0]); } }} />
                    </div>

                    {(settings.admin_logo || adminLogoPreview) && (
                      <button onClick={() => { update('admin_logo', ''); setAdminLogoPreview(null); }} style={{ marginTop: '12px', width: '100%', padding: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', color: '#DC2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                        🗑 Remove Logo (Use Default)
                      </button>
                    )}
                  </div>
                </div>

                {/* Brand Name & Tagline */}
                <div style={{ display: 'grid', gap: '20px' }}>
                  <h3 style={{ fontFamily: 'Montserrat', fontSize: '15px', fontWeight: 700, color: 'white' }}>Brand Text</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>Brand Name (Navbar text)</label>
                      <input type="text" value={settings.site_name || ''} onChange={e => update('site_name', e.target.value)} style={inputStyle} placeholder="CSK SPORTSWEAR" />
                    </div>
                    <div>
                      <label style={labelStyle}>Tagline (Under logo)</label>
                      <input type="text" value={settings.site_tagline || ''} onChange={e => update('site_tagline', e.target.value)} style={inputStyle} placeholder="Chennai, Tamil Nadu" />
                    </div>
                  </div>

                  <div style={{ background: 'rgba(0,102,255,0.05)', border: '1px solid rgba(0,102,255,0.15)', borderRadius: '10px', padding: '16px', fontSize: '13px', color: '#94A3B8', lineHeight: 1.6 }}>
                    💡 <strong style={{ color: 'white' }}>Tip:</strong> Upload a PNG with transparent background for best results. Logo will auto-resize to fit navbar and sidebar. After uploading, page will auto-save.
                  </div>
                </div>
              </div>
            )}

            {/* ── HERO TAB ── */}
            {activeTab === 'hero' && (
              <div>
                <h2 style={{ fontFamily: 'Montserrat', fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '24px' }}>🏠 Hero Section</h2>
                <div style={{ display: 'grid', gap: '20px' }}>
                  <div>
                    <label style={labelStyle}>Badge Text</label>
                    <input type="text" value={settings.hero_badge || ''} onChange={e => update('hero_badge', e.target.value)} style={inputStyle} placeholder="CHENNAI'S PREMIER SPORTS WEAR" />
                  </div>
                  <div>
                    <label style={labelStyle}>Hero Title (use \n for new line)</label>
                    <textarea rows={3} value={settings.hero_title || ''} onChange={e => update('hero_title', e.target.value)} style={textareaStyle} placeholder="WEAR THE CHAMPION SPIRIT" />
                  </div>
                  <div>
                    <label style={labelStyle}>Hero Subtitle</label>
                    <textarea rows={3} value={settings.hero_subtitle || ''} onChange={e => update('hero_subtitle', e.target.value)} style={textareaStyle} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>Primary Button</label>
                      <input type="text" value={settings.hero_btn1 || ''} onChange={e => update('hero_btn1', e.target.value)} style={inputStyle} placeholder="Shop Now" />
                    </div>
                    <div>
                      <label style={labelStyle}>Secondary Button</label>
                      <input type="text" value={settings.hero_btn2 || ''} onChange={e => update('hero_btn2', e.target.value)} style={inputStyle} placeholder="Get Quote" />
                    </div>
                    <div>
                      <label style={labelStyle}>Scrolling Marquee Text</label>
                      <textarea rows={3} value={settings.marquee_text || ''} onChange={e => update('marquee_text', e.target.value)} style={textareaStyle} placeholder="★ CRICKET JERSEY ★ FOOTBALL KIT ★ BADMINTON WEAR" />
                      <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px' }}>
                        💡 Use ★ as separator between items
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── CONTACT TAB ── */}
            {activeTab === 'contact' && (
              <div>
                <h2 style={{ fontFamily: 'Montserrat', fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '24px' }}>📞 Contact Information</h2>
                <div style={{ display: 'grid', gap: '20px' }}>
                  <div>
                    <label style={labelStyle}>WhatsApp Number (91XXXXXXXXXX format)</label>
                    <input type="text" value={settings.whatsapp_number || ''} onChange={e => update('whatsapp_number', e.target.value)} style={inputStyle} placeholder="919876543210" />
                    <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px' }}>⚠️ No + sign, no spaces. Example: 919876543210</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Phone (Display)</label>
                    <input type="text" value={settings.phone || ''} onChange={e => update('phone', e.target.value)} style={inputStyle} placeholder="+91 98765 43210" />
                  </div>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input type="email" value={settings.email || ''} onChange={e => update('email', e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Address</label>
                    <textarea rows={2} value={settings.address || ''} onChange={e => update('address', e.target.value)} style={textareaStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Working Hours</label>
                    <input type="text" value={settings.working_hours || ''} onChange={e => update('working_hours', e.target.value)} style={inputStyle} placeholder="Mon–Sat: 9AM – 6PM" />
                  </div>
                </div>
              </div>
            )}

            {/* ── ABOUT TAB ── */}
            {activeTab === 'about' && (
              <div>
                <h2 style={{ fontFamily: 'Montserrat', fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '24px' }}>ℹ️ About Section</h2>
                <div style={{ display: 'grid', gap: '20px' }}>
                  <div>
                    <label style={labelStyle}>About Title</label>
                    <input type="text" value={settings.about_title || ''} onChange={e => update('about_title', e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>About Content (Para 1)</label>
                    <textarea rows={4} value={settings.about_content || ''} onChange={e => update('about_content', e.target.value)} style={textareaStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>About Content (Para 2)</label>
                    <textarea rows={4} value={settings.about_content2 || ''} onChange={e => update('about_content2', e.target.value)} style={textareaStyle} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>Founded Year</label>
                      <input type="text" value={settings.founded || ''} onChange={e => update('founded', e.target.value)} style={inputStyle} placeholder="2015" />
                    </div>
                    <div>
                      <label style={labelStyle}>Location</label>
                      <input type="text" value={settings.location || ''} onChange={e => update('location', e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Speciality</label>
                      <input type="text" value={settings.speciality || ''} onChange={e => update('speciality', e.target.value)} style={inputStyle} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── STATS TAB ── */}
            {activeTab === 'stats' && (
              <div>
                <h2 style={{ fontFamily: 'Montserrat', fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '24px' }}>📊 Statistics</h2>
                <div style={{ background: 'rgba(0,102,255,0.05)', border: '1px solid rgba(0,102,255,0.15)', borderRadius: '10px', padding: '14px', fontSize: '13px', color: '#94A3B8', marginBottom: '24px' }}>
                  💡 Shows in Hero section and About section
                </div>
                <div className="stats-row" style={{ display: 'grid', gap: '16px' }}>
                  {[
                    { key: 'stats_teams', label: 'Teams Served', placeholder: '500+' },
                    { key: 'stats_jerseys', label: 'Jerseys Delivered', placeholder: '10K+' },
                    { key: 'stats_sports', label: 'Sports Covered', placeholder: '15+' },
                    { key: 'stats_rating', label: 'Rating', placeholder: '5★' },
                  ].map(s => (
                    <div key={s.key} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '16px', alignItems: 'center' }}>
                      <label style={{ ...labelStyle, marginBottom: 0 }}>{s.label}</label>
                      <input type="text" value={settings[s.key] || ''} onChange={e => update(s.key, e.target.value)} style={inputStyle} placeholder={s.placeholder} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── FOOTER TAB ── */}
            {activeTab === 'footer' && (
              <div>
                <h2 style={{ fontFamily: 'Montserrat', fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '24px' }}>📄 Footer</h2>
                <div style={{ display: 'grid', gap: '20px' }}>
                  <div>
                    <label style={labelStyle}>Copyright Text</label>
                    <input type="text" value={settings.footer_text || ''} onChange={e => update('footer_text', e.target.value)} style={inputStyle} placeholder="© 2026 CSK Sportswear. All rights reserved." />
                  </div>
                  <div>
                    <label style={labelStyle}>Footer Tagline</label>
                    <input type="text" value={settings.footer_tagline || ''} onChange={e => update('footer_tagline', e.target.value)} style={inputStyle} placeholder="Chennai, Tamil Nadu" />
                  </div>
                  <div>
                    <label style={labelStyle}>Developer Credit</label>
                    <input type="text" value={settings.dev_credit || ''} onChange={e => update('dev_credit', e.target.value)} style={inputStyle} placeholder="Powered by Veltrix Technologies" />
                  </div>
                </div>
              </div>
            )}

            {/* Save Bottom */}
            <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: '12px', justifyContent: 'flex-end', alignItems: 'center' }}>
              {saved && <div style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: '8px', padding: '10px 16px', color: '#16A34A', fontSize: '13px', fontWeight: 600 }}>✅ Saved!</div>}
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 32px', background: 'linear-gradient(135deg, #0066FF, #0052CC)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving...' : '💾 Save All Changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SiteSettings;
